import React, { useRef, useMemo } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { useScroll, Text, Image } from '@react-three/drei';
import * as THREE from 'three';

// ==========================================
// SECTION: SHARED GEOMETRIES (OPTIMIZATION)
// ==========================================

// --- Environment Geometries (Pillar, Lights, Rods) ---
const TORUS_GEO_PILLAR = new THREE.TorusGeometry(2.25, 0.08, 16, 32);
const SPHERE_GEO_DOT = new THREE.SphereGeometry(0.08, 8, 8);
const SPHERE_GEO_RIM = new THREE.SphereGeometry(0.2, 16, 16);
const SPHERE_GEO_TOP = new THREE.SphereGeometry(1.5, 32, 32);
const CYL_GEO_ROD = new THREE.CylinderGeometry(0.04, 0.04, 8);

// --- Airplane Specific Geometries ---
// Fuselage Body
const GEO_FUSELAGE = new THREE.CylinderGeometry(0.35, 0.25, 1.8, 12);
GEO_FUSELAGE.rotateZ(Math.PI / 2);

// Nose Cone
const GEO_NOSE = new THREE.ConeGeometry(0.35, 0.5, 12);
GEO_NOSE.rotateZ(-Math.PI / 2);

// Tail Cone
const GEO_TAIL_SECTION = new THREE.CylinderGeometry(0.25, 0.1, 0.8, 10);
GEO_TAIL_SECTION.rotateZ(Math.PI / 2);

// Wings & Stabilizers
const GEO_WING_MAIN = new THREE.BoxGeometry(0.7, 0.08, 3.2); // Wide wingspan
const GEO_TAIL_H = new THREE.BoxGeometry(0.4, 0.08, 1.4); // Rear horizontal wings
const GEO_TAIL_V = new THREE.BoxGeometry(0.5, 0.6, 0.08); // Vertical tail fin

// Propeller Parts
const GEO_PROP_HUB = new THREE.CylinderGeometry(0.1, 0.1, 0.2, 8);
GEO_PROP_HUB.rotateZ(Math.PI / 2);
const GEO_PROP_BLADE = new THREE.BoxGeometry(0.08, 1.6, 0.15);

// Cockpit / Open Seat Parts
const GEO_SEAT_VOID = new THREE.BoxGeometry(0.5, 0.35, 0.45); // Represents the open hole
const GEO_WINDSHIELD = new THREE.BoxGeometry(0.02, 0.25, 0.4);
GEO_WINDSHIELD.rotateZ(-0.3); // Slant back

// ==========================================
// SECTION: SHARED MATERIALS
// ==========================================
const MAT_ROD_DARK = new THREE.MeshStandardMaterial({ color: "#111", metalness: 0.9, roughness: 0.1 });
const MAT_PROP_WHITE = new THREE.MeshBasicMaterial({ color: "white" });
const MAT_RING_CYAN = new THREE.MeshBasicMaterial({ color: "cyan", toneMapped: false });
const MAT_CANOPY_WIRE = new THREE.MeshBasicMaterial({ color: "#ff007f", wireframe: true });
const MAT_SEAT_DARK = new THREE.MeshStandardMaterial({ color: "#050505", roughness: 1.0 }); // Dark matte seat
const MAT_GLASS = new THREE.MeshPhysicalMaterial({
    color: "#aaddff", transmission: 0.6, opacity: 0.5, transparent: true, roughness: 0.2, thickness: 0.1
});

// ==========================================
// SECTION: CONFIGURATION DATA
// ==========================================
const NEON_COLORS = [
    "#FF0000", "#00FFFF", "#FFFF00", "#FF00FF",
    "#00FF00", "#FFFFFF", "#800080", "#0000FF", "#FFA500"
];

import titleSponsorLogo from '../assets/sponsers/title_sponser.png';
import event1Logo from '../assets/sponsers/event1.png';
import event2Logo from '../assets/sponsers/event2.png';
import event3Logo from '../assets/sponsers/event3.png';
import logistics1Logo from '../assets/sponsers/logistics1.png';
import logistics2Logo from '../assets/sponsers/logistics2.png';
import mediaLogo from '../assets/sponsers/media.webp';
import digitalMediaLogo from '../assets/sponsers/digital_media.png';
import travelLogo from '../assets/sponsers/travel.svg';

const CATEGORIES = [
    { name: "Title Sponsor", logo: titleSponsorLogo },
    { name: "Event Partner", logo: event1Logo },
    { name: "Event Partner", logo: event2Logo },
    { name: "Event Partner", logo: event3Logo },
    { name: "Logistics Partner", logo: logistics1Logo },
    { name: "Logistics Partner", logo: logistics2Logo },
    { name: "Media Partner", logo: mediaLogo },
    { name: "Digital Media Partner", logo: digitalMediaLogo, isCircular: true },
    { name: "Travel Partner", logo: travelLogo }
];

const SPONSORS = CATEGORIES.map((cat, id) => ({
    id,
    color: NEON_COLORS[id],
    category: cat.name,
    logo: cat.logo,
    isCircular: cat.isCircular || false
}));

// ==========================================
// SECTION: SUB-COMPONENTS
// ==========================================

// --- 1. Pillar Lights (Rings on central pole) ---
function PillarLight({ position, index }) {
    const matRef = useRef();
    useFrame((state) => {
        if (matRef.current) {
            const hue = (state.clock.elapsedTime * 0.5 + index * 0.15) % 1;
            matRef.current.color.setHSL(hue, 1, 0.5);
        }
    });
    return (
        <mesh position={position} rotation={[Math.PI / 2, 0, 0]} geometry={TORUS_GEO_PILLAR}>
            <meshBasicMaterial ref={matRef} toneMapped={false} />
        </mesh>
    );
}

// --- 2. Top Hub (The glowing ball at the very top) ---
function TopHub() {
    const matRef = useRef();
    useFrame((state) => {
        if (matRef.current) {
            const time = state.clock.elapsedTime;
            const hue = (time * 0.1) % 1;
            matRef.current.color.setHSL(hue, 1, 0.5);
            matRef.current.emissive.setHSL(hue, 1, 0.5);
        }
    });

    return (
        <mesh position={[0, 12.5, 0]} geometry={SPHERE_GEO_TOP}>
            <meshStandardMaterial
                ref={matRef}
                emissiveIntensity={2}
                toneMapped={false}
            />
        </mesh>
    )
}

// --- 3. Animated Canopy Bulbs (Rim lights) ---
function AnimatedBulb({ position, index }) {
    const matRef = useRef();
    useFrame((state) => {
        if (matRef.current) {
            const time = state.clock.elapsedTime;
            const hue = (time * 0.5 + index * 0.1) % 1;
            matRef.current.color.setHSL(hue, 1, 0.6);

            const blink = Math.sin(time * 8 + index * 2) > 0 ? 1 : 0.3;
            matRef.current.emissive.setHSL(hue, 1, 0.6);
            matRef.current.emissiveIntensity = blink * 3;
        }
    });

    return (
        <mesh position={position} geometry={SPHERE_GEO_RIM}>
            <meshStandardMaterial ref={matRef} toneMapped={false} />
        </mesh>
    )
}

function CanopyLights({ count, radius, y }) {
    const positions = useMemo(() => {
        const pos = [];
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            pos.push([Math.cos(angle) * radius, 0, Math.sin(angle) * radius]);
        }
        return pos;
    }, [count, radius]);

    return (
        <group position={[0, y, 0]}>
            {positions.map((pos, i) => (
                <AnimatedBulb key={i} position={pos} index={i} />
            ))}
        </group>
    )
}

// --- 4. Rod Chaser Lights (Vertical connecting poles) ---
function RodChaser({ height = 8, count = 10, index }) {
    const lightRefs = useRef([]);

    useFrame((state) => {
        const time = state.clock.elapsedTime;
        const hue = (time * 0.2 + index * 0.1) % 1;
        const color = new THREE.Color().setHSL(hue, 1, 0.5);
        const speed = 10;
        const pos = (time * speed) % (count + 4);

        for (let i = 0; i < count; i++) {
            const ref = lightRefs.current[i];
            if (ref) {
                //  const targetIndex = (count - 1) - i;
                // Keeping it simple
                const isLit = Math.abs(pos - ((count - 1) - i)) < 1.0;
                if (isLit) ref.color.copy(color);
                else ref.color.setHex(0x222222);
            }
        }
    });

    return (
        <group position={[0, 4, 0]}>
            <mesh geometry={CYL_GEO_ROD} material={MAT_ROD_DARK} />
            {Array.from({ length: count }).map((_, i) => (
                <mesh
                    key={i}
                    position={[0, (i - (count - 1) / 2) * (height / count), 0]}
                    geometry={SPHERE_GEO_DOT}
                >
                    <meshBasicMaterial
                        ref={el => lightRefs.current[i] = el}
                        toneMapped={false}
                    />
                </mesh>
            ))}
        </group>
    )
}

// --- Utility: Auto-Scaling Glowing Logo Banner ---
function LogoBanner({ logo, isCircular }) {
    const texture = useLoader(THREE.TextureLoader, logo);

    // Automatically preserve image aspect ratio and fit into a maximum 3.6x1.8 bounding box
    const aspect = texture?.image ? (texture.image.width / texture.image.height) : 2;

    let w = 3.6;
    let h = 3.6 / aspect;
    if (h > 1.8) {
        h = 1.8;
        w = 1.8 * aspect;
    }

    // Boost brightness of texture colors for bloom effect and make transparent natively
    const glowColor = new THREE.Color(2.5, 2.5, 2.5);

    if (isCircular) {
        return (
            <mesh>
                <circleGeometry args={[0.9, 64]} />
                <meshBasicMaterial map={texture} transparent side={THREE.DoubleSide} toneMapped={false} color={glowColor} depthWrite={false} alphaTest={0.01} />
            </mesh>
        );
    }

    return (
        <mesh>
            <planeGeometry args={[w, h]} />
            <meshBasicMaterial map={texture} transparent side={THREE.DoubleSide} toneMapped={false} color={glowColor} depthWrite={false} alphaTest={0.01} />
        </mesh>
    );
}

// --- 5. Airplane Component (Vehicle Logic) ---
function Plane({ index, total, radius, color: initialColor, category, logo, isCircular }) {
    const groupRef = useRef();
    const propellerRef = useRef();
    const bannerGroupRef = useRef();

    const sharedWireMat = useMemo(() => new THREE.MeshBasicMaterial({
        color: initialColor,
        wireframe: true,
        transparent: true,
        opacity: 0.3
    }), [initialColor]);

    // Shared Solid Material for Body, Wings, and Tail
    const sharedSolidMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: "#111",
        emissive: initialColor,
        emissiveIntensity: 0.5,
        roughness: 0.4,
        metalness: 0.6
    }), [initialColor]);

    const lightRef = useRef();

    const angle = (index / total) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;

    useFrame((state) => {
        const time = state.clock.elapsedTime;

        // Bobbing Animation
        if (groupRef.current) {
            const bob = Math.sin(time * 2 + index) * 0.5;
            groupRef.current.position.y = bob;
            groupRef.current.rotation.y = -angle - Math.PI / 2;
            groupRef.current.rotation.z = bob * 0.2;
        }

        // Propeller Spin
        if (propellerRef.current) {
            propellerRef.current.rotation.x += 0.4;
        }

        // Color Cycling
        const hue = (time * 0.2 + index * 0.1) % 1;
        const dynamicColor = new THREE.Color().setHSL(hue, 1, 0.5);

        sharedWireMat.color.copy(dynamicColor);

        const blinkSpeed = 5;
        const pulse = Math.sin(time * blinkSpeed) * 0.5 + 0.5;

        // Update Shared Solid Material
        sharedSolidMat.emissive.copy(dynamicColor);
        sharedSolidMat.emissiveIntensity = (0.5 + pulse);

        if (lightRef.current) {
            lightRef.current.color.copy(dynamicColor);
            lightRef.current.intensity = (1 + pulse * 2);
        }

        // Billboard Effect: Make Banner Group face camera
        if (bannerGroupRef.current) {
            // Because the object is inside a rotating parent, calculate the world position of the camera
            // relative to the parent and face it, but simplifying it: lookAt works in world space.
            bannerGroupRef.current.lookAt(state.camera.position);
        }
    });

    return (
        <group position={[x, 0, z]}>
            <group ref={groupRef}>
                {/* Connecting Rod */}
                <RodChaser height={8} index={index} />

                {/* --- AIRPLANE ASSEMBLY --- */}
                <group position={[0, 0, 0]} scale={1.2}>
                    {/* Main Fuselage (Wireframe Skin) */}
                    <mesh geometry={GEO_FUSELAGE} material={sharedWireMat} />

                    {/* Inner Glow Core - Uses Shared Solid Mat */}
                    <mesh geometry={GEO_FUSELAGE} scale={[0.95, 0.95, 0.95]} material={sharedSolidMat} />

                    {/* Nose Cone */}
                    <mesh position={[1.15, 0, 0]} geometry={GEO_NOSE} material={sharedWireMat} />

                    {/* Tail Cone */}
                    <mesh position={[-1.3, 0, 0]} geometry={GEO_TAIL_SECTION} material={sharedWireMat} />

                    {/* Main Wings - NOW SOLID */}
                    <mesh position={[0.3, 0.15, 0]} geometry={GEO_WING_MAIN} material={sharedSolidMat} />

                    {/* Tail Stabilizers - NOW SOLID */}
                    <group position={[-1.5, 0, 0]}>
                        {/* Horizontal */}
                        <mesh position={[0, 0.1, 0]} geometry={GEO_TAIL_H} material={sharedSolidMat} />
                        {/* Vertical */}
                        <mesh position={[0, 0.3, 0]} geometry={GEO_TAIL_V} material={sharedSolidMat} />
                    </group>

                    {/* Open Seat / Cockpit */}
                    {/* Dark void to simulate seat hole */}
                    <mesh position={[-0.3, 0.2, 0]} geometry={GEO_SEAT_VOID} material={MAT_SEAT_DARK} />
                    {/* Windshield */}
                    <mesh position={[-0.02, 0.3, 0]} geometry={GEO_WINDSHIELD} material={MAT_GLASS} />

                    {/* Propeller */}
                    <group position={[1.4, 0, 0]} ref={propellerRef}>
                        <mesh geometry={GEO_PROP_HUB} material={MAT_PROP_WHITE} />
                        <mesh geometry={GEO_PROP_BLADE} material={MAT_PROP_WHITE} />
                        <mesh rotation={[Math.PI / 2, 0, 0]} geometry={GEO_PROP_BLADE} material={MAT_PROP_WHITE} />
                    </group>

                    {/* Billboard Group: Holds both Text and Logo, rotates to Camera */}
                    <group position={[0, 2.0, 0]} ref={bannerGroupRef}>
                        {/* Wrapper to shift perfectly 0.8 units OUTWARD towards camera, clearing the central pole */}
                        <group position={[0, 0, 0.8]}>
                            {/* Glowing Category Text (Cyan) */}
                            <Text
                                position={[0, 0.9, 0]}
                                fontSize={0.5}
                                color="#fff" // White core 
                                anchorX="center"
                                anchorY="bottom"
                            >
                                {category}
                                <meshBasicMaterial toneMapped={false} />
                                {/* Native 3D Neon Glow Layer */}
                                <Text
                                    position={[0, 0, -0.05]}
                                    fontSize={0.5}
                                    color="#00F5FF"
                                    anchorX="center"
                                    anchorY="bottom"
                                    outlineWidth={0.06}
                                    outlineColor="#00F5FF"
                                    outlineOpacity={1}
                                >
                                    {category}
                                    <meshBasicMaterial toneMapped={false} transparent opacity={0.6} depthWrite={false} />
                                </Text>
                            </Text>

                            {/* Sponsor Logo Banner */}
                            <group position={[0, 0, 0]}>
                                {logo ? (
                                    <LogoBanner logo={logo} isCircular={isCircular} />
                                ) : (
                                    <group>
                                        <mesh>
                                            <planeGeometry args={[1.8, 0.9]} />
                                            <meshBasicMaterial color="#333" />
                                        </mesh>
                                        <Text
                                            position={[0, 0, 0.01]}
                                            fontSize={0.25}
                                            color="#FFF"
                                            anchorX="center"
                                            anchorY="middle"
                                        >
                                            LOGO SPACE
                                        </Text>
                                        <mesh position={[0, 0, -0.01]}>
                                            <planeGeometry args={[1.85, 0.95]} />
                                            <meshBasicMaterial color="#000" />
                                        </mesh>
                                    </group>
                                )}
                            </group>
                        </group>
                    </group>

                    {/* Under-Plane Glow Light */}
                    <pointLight ref={lightRef} distance={8} intensity={5} color={initialColor} />
                </group>
            </group>
        </group>
    );
}

// ==========================================
// SECTION: MAIN CAROUSEL STRUCTURE
// ==========================================
const SponsorCarousel = () => {
    const carouselRef = useRef();
    const scroll = useScroll(); // Use global scroll from context
    const RADIUS = 8;
    const NUM_PLANES = 9;
    const CANOPY_BOTTOM_Y = 7.5;

    const matPlatform = useMemo(() => new THREE.MeshPhysicalMaterial({
        color: "#222222", roughness: 0.6, metalness: 0, transmission: 0.8, thickness: 2, clearcoat: 0, ior: 1.5
    }), []);

    const matPillar = useMemo(() => new THREE.MeshPhysicalMaterial({
        color: "#111111", roughness: 0.6, metalness: 0, transmission: 0.9, thickness: 3, clearcoat: 0, ior: 1.5
    }), []);

    const matCanopy = useMemo(() => new THREE.MeshPhysicalMaterial({
        color: "#220022", roughness: 0.6, metalness: 0, transmission: 0.8, thickness: 1, clearcoat: 0, side: THREE.DoubleSide
    }), []);

    useFrame((state, delta) => {
        // Map Scroll Offset to Rotation
        // We know the Sponsors section corresponds to roughly offset 0.5 - 0.9 (depending on ScrollControls pages)
        // For simplicity, just use raw offset * speed
        const r = scroll.offset;
        let targetRotation = -r * Math.PI * 8; // Faster spin with scroll

        if (carouselRef.current) {
            // Smooth Rotation
            carouselRef.current.rotation.y = THREE.MathUtils.damp(carouselRef.current.rotation.y, targetRotation, 1.0, delta);
        }
    });

    return (
        <group ref={carouselRef}>
            {/* --- PART: Platform Base --- */}
            <mesh position={[0, -2, 0]} material={matPlatform}>
                <cylinderGeometry args={[RADIUS + 1, RADIUS + 1, 0.5, 64]} />
            </mesh>

            {/* --- PART: Neon Ring around Base --- */}
            <mesh position={[0, -2, 0]} rotation={[Math.PI / 2, 0, 0]} material={MAT_RING_CYAN}>
                <torusGeometry args={[RADIUS + 1, 0.15, 16, 100]} />
            </mesh>

            {/* --- PART: Central Pillar --- */}
            <mesh position={[0, 4, 0]} material={matPillar}>
                <cylinderGeometry args={[2, 2, 12, 32]} />
            </mesh>

            {/* --- PART: Pillar Lights (Animated Rings) --- */}
            {Array.from({ length: 6 }).map((_, i) => (
                <PillarLight key={i} position={[0, i * 1.8 - 1, 0]} index={i} />
            ))}

            {/* --- PART: Canopy (Roof) Structure --- */}
            <mesh position={[0, 10, 0]} material={matCanopy}>
                <coneGeometry args={[RADIUS + 3, 5, 32]} />
            </mesh>
            <mesh position={[0, 10, 0]} material={MAT_CANOPY_WIRE}>
                <coneGeometry args={[RADIUS + 3.1, 5.1, 16, 1, true]} />
            </mesh>

            {/* --- PART: Canopy Lighting (Inside & Rim) --- */}
            <pointLight position={[0, 9, 0]} intensity={10} color="#ffaa00" distance={20} decay={2} />
            <CanopyLights count={24} radius={RADIUS + 3} y={CANOPY_BOTTOM_Y} />

            {/* --- PART: Top Ornament --- */}
            <TopHub />
            <pointLight position={[0, 12, 0]} color="#ff9900" intensity={5} distance={20} />

            {/* --- PART: Airplanes (The Riders) --- */}
            {SPONSORS.map((ev, i) => (
                <Plane key={i} index={i} total={NUM_PLANES} radius={RADIUS} color={ev.color} category={ev.category} logo={ev.logo} />
            ))}
        </group>
    )
}

export default SponsorCarousel;
